import { statusEffectDefaults, zoneDefaults } from '../../lib/effects/defaults';
import { backgroundDefaults, itemGroupDefaults } from '../../lib/hb/defaults';
import { coreStatDefaults, perkDefaults, skillDefaults } from '../../lib/skills/defaults';
import { newAlly, newBeatChain, newNpc, newPronounSet } from '../../lib/storyDefaults';
import {
  crewDefaults,
  holdoutDefaults,
  humanDefaults,
  keyPartDefaults,
  occupiedTellDefaults,
  siteKindDefaults,
  techTierDefaults,
} from '../../lib/s2/defaults';
import type { Schemas } from '@argzombie/api-client';
import type { CollectionKey } from '../../lib/contentConfig';
import {
  bossDefaults,
  cosmeticDefaults,
  missionDefaults,
  survivorTraitDefaults,
  triggerCurrencyDefaults,
  triggerDefaults,
  triggerEncounterDefaults,
  triggerEventDefaults,
  triggerHealthDefaults,
  triggerItemsDefaults,
  triggerRaidDefaults,
  triggerRulesDefaults,
} from '../../lib/teamsDefaults';
import { newArenaTemplate, newVehicle } from '../../lib/s3/defaults';
import { newArc } from '../../lib/s4/defaults';
import { ARMOR_WIND_DEFAULTS, newWindTier, triggerWindDefaults } from '../../lib/wind/defaults';
import {
  TRIGGER_ACTIONS,
  TRIGGER_BLOCKS,
  type ArmorDef,
  type ArmorRepairDef,
  type BossDef,
  type Bundle,
  type MissionDef,
  type ThrowableDef,
  type TriggerDef,
  type TriggerEncounterBlock,
} from '../../lib/validate';

// The Teams on the Map object blocks (team rules, discovery, ...) have their C# defaults in lib/teamsDefaults.ts.

export const newWeapon = (): Schemas['WeaponDef'] => ({
  damage: 10,
  rangeM: 1.5,
  fireIntervalS: 0.5,
  magazineSize: 0,
  reloadS: 0,
  ammoItemId: null,
  pellets: 1,
  spreadDeg: 0,
  meleeArcDeg: 90,
  durabilityShots: null,
  recoverChance: 0,
  headshotChance: 0,
  pierce: 0,
  pierceFalloff: 0.7,
  knockbackM: 0.25,
  heldModel: null,
  stealthKill: false,
  stealthKillS: 0.4,
  stealthKillNoiseM: 0,
  audio: null, // the class sound until a cue is picked (audio plan §4 item 5)
});

/**
 * A new armor piece (raid overhaul 06 §A): one bite-block per raid, no padding, the C# guard, speed and noise defaults,
 * no reduction (advanced), and no repair. WIND (contract 3.4): no wind guard, 1x wind costs and bite odds; `bonusHealth` is
 * the old field, kept one content version.
 */
export const newArmor = (): ArmorDef => ({
  reduction: 0,
  durability: 60,
  bonusHealth: 0,
  ...ARMOR_WIND_DEFAULTS,
  blockGuardS: 0.35,
  moveSpeedMult: 1,
  movementNoiseAddM: 0,
  repair: null,
});

/** A repair as the form turns it on: no inputs yet (the validator asks for some), no module. */
export const newArmorRepair = (): ArmorRepairDef => ({
  inputs: [],
  restores: 30,
  requiredModuleId: null,
  requiredModuleLevel: 0,
});

/**
 * A new throwable (raid overhaul Phase 5): a blast like the seed pipe bomb, a bit weaker. Switching the kind is up to the
 * editor; the validator says which numbers each kind needs.
 */
export const newThrowable = (): ThrowableDef => ({
  kind: 'blast',
  throwRangeM: 12,
  fuseS: 2.5,
  bounce: 0.35,
  radiusM: 4,
  damage: 100,
  falloffMin: 0.2,
  knockbackM: 1,
  noiseRadiusM: 60,
  lureS: 0,
  pulseS: 0,
  fireS: 0,
  fireDps: 0,
  lightRadiusM: 0,
  friendlyFireMult: 0.5,
});

/** shared-cs `RaidRulesDef` defaults for the loadout timings, shown when an older draft has no value yet. */
export const RAID_LOADOUT_DEFAULTS = { swapSeconds: 0.35, rummageSeconds: 1.5 } as const;

/** shared-cs `RaidRulesDef` defaults for the fog of war (ADR 0026), shown when an older draft has no value yet. */
export const RAID_FOG_DEFAULTS = { visionRadiusM: 13, ghostMarkerSeconds: 10 } as const;

/** A blank-but-sensible new entry per collection (C# defaults), used by the editor's Add button. */
export const newEntry: Record<CollectionKey, (id: string) => Record<string, unknown>> = {
  items: (id) => ({
    id,
    name: 'New item',
    category: 'material',
    weightKg: 0.5,
    noiseRadiusM: null,
    stackSize: 1,
    carryBonusKg: null,
    weapon: null,
    armor: null,
    throwable: null,
    tags: [],
    heal: 0,
    energy: 0,
    value: 0,
  }),
  lootTables: (id) => ({ id, entries: [] }),
  recipes: (id) => ({
    id,
    name: 'New recipe',
    inputs: [],
    outputs: [],
    requiredModuleId: null,
    requiredModuleLevel: 0,
  }),
  baseModules: (id) => ({
    id,
    name: 'New module',
    description: '',
    levels: [
      {
        level: 1,
        cost: [],
        buildSeconds: 60,
        storageBonusKg: 0,
        waterPerDay: 0,
        healPerDay: 0,
        recruitChanceBonus: 0,
        repairBonus: 0,
      },
    ],
  }),
  zombies: (id) => ({
    id,
    name: 'New zombie',
    maxHealth: 50,
    moveSpeedMps: 1.5,
    attackDamage: 10,
    attackCooldownS: 1,
    hearingMultiplier: 1,
    sightRangeM: 8,
    knockbackForce: 0,
    headshotMult: 1,
    headArmor: false,
    knockbackResist: 0,
    sightHalfAngleDeg: 25,
    noticeFarS: 0.9,
    noticeNearS: 0.25,
    sweepDeg: 35,
    sweepPeriodMinS: 5,
    sweepPeriodMaxS: 7,
    stealthKillS: 0,
    stealthKillNoiseM: 0,
    stealthImmune: false,
  }),
  categories: (id) => ({
    id,
    name: 'New category',
    lootTableId: '',
    difficulty: 1,
    lootRollsPerRoom: 1.5,
    osmTags: [],
    likelyLoot: [],
  }),
  winds: (id) => newWindTier(id),
  roomTemplates: (id) => ({
    id,
    name: 'New room',
    category: 'shared',
    weight: 1,
    minSideM: 3,
    isEntry: false,
    containersMin: 0,
    containersMax: 0,
    containerKinds: [],
    zombiesMin: 0,
    zombiesMax: 0,
  }),
  recruitRoles: (id) => ({
    id,
    name: 'New role',
    description: '',
    effect: 'heal',
    amount: 0,
    itemId: null,
  }),
  survivorTraits: (id) => ({ ...survivorTraitDefaults(), id, name: 'New trait' }),
  // A coming-soon teaser with the C# defaults; slug and codePrefix need editing before it validates.
  bosses: (id) => ({
    ...bossDefaults(),
    id,
    slug: id.replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, ''),
    codePrefix: 'NEWBOSS',
    name: 'New boss',
  }),
  // Scan triggers (ADR 0029). A new trigger is a coming-soon item grant with the C# rules (once per run); pick the
  // items (the validator asks for at least one) or switch the action.
  triggers: (id) => ({
    ...triggerDefaults(),
    id,
    action: 'grant_items',
    title: 'New trigger',
    codePrefix: 'CODE',
    items: triggerItemsDefaults(),
  }),
  // The order needs to be unique; the validator says so if it isn't.
  missions: (id) => ({ ...missionDefaults(), id, title: 'New mission' }),
  cosmetics: (id) => ({ ...cosmeticDefaults(), id, name: 'New cosmetic', kind: 'outfit' }),
  beatChains: (id) => newBeatChain(id),
  allies: (id) => newAlly(id),
  scenarios: (id) => ({
    id,
    kind: 'interior',
    title: 'New scenario',
    building: { categories: [], kinds: [], minRooms: 0 },
    setPieces: [],
    actors: [],
    objectives: [],
    triggers: [],
    lose: [],
    onWin: [],
    onLose: [],
  }),
  setPieces: (id) => ({
    id,
    name: 'New set piece',
    templateId: '',
    categories: [],
    footprint: { w: 6, d: 5 },
    rotations: [0, 90],
    anchors: [],
    props: [],
    zombies: [],
    placementVersion: 1,
  }),
  worldFlags: (key) => ({
    key,
    type: 'enum',
    values: [],
    scope: 'run',
    badge: '',
    priority: 0,
    description: '',
  }),
  npcs: (id) => newNpc(id),
  pronounSets: (id) => newPronounSet(id),
  // Stories & Tech Step 2: the C# defaults (lib/s2/defaults.ts) with a name, so a new entry is recognisable.
  crews: (id) => ({ ...crewDefaults(id), name: 'New crew' }),
  humans: (id) => humanDefaults(id),
  tells: (id) => ({ ...occupiedTellDefaults(id), gate: 'yard', disposition: 'normal' }),
  holdouts: (id) => ({ ...holdoutDefaults(id), name: 'New holdout' }),
  // Keyed by its number: the editor passes the next free tier as the id.
  techTiers: (tier) => ({ ...techTierDefaults(Number(tier) || 0), name: 'New tier' }),
  siteKinds: (id) => ({ ...siteKindDefaults(id), name: 'New site kind' }),
  keyParts: (id) => ({ ...keyPartDefaults(id), name: 'New key part' }),
  vehicles: (id) => newVehicle(id),
  arenaTemplates: (id) => newArenaTemplate(id),
  arcs: (id) => newArc(id),
  // Outta-Ten (ADR 0066): the ids are code, so a new row starts from the typed id; the validator says what's missing.
  coreStats: (id) => ({ ...coreStatDefaults(), id, name: 'New stat', rustClass: 'body' }),
  skills: (id) => ({ ...skillDefaults(), id, name: 'New skill', rustClass: 'skill' }),
  perks: (id) => ({ ...perkDefaults(), id, name: 'New perk' }),
  backgrounds: (id) => ({ ...backgroundDefaults(), id }),
  statusEffects: (id) => ({
    ...statusEffectDefaults(id),
    name: 'New effect',
    family: id,
    modifiers: [{ key: 'stat.nrv', op: 'add', value: 1 }],
    duration: { kind: 'timed', hours: 12 },
  }),
  zones: (id) => ({ ...zoneDefaults(id.toLowerCase()), name: 'New zone' }),
  storageGroups: (id) => ({ ...itemGroupDefaults(id.toLowerCase()), name: 'New group' }),
  buildCategories: (id) => ({ ...itemGroupDefaults(id.toLowerCase()), name: 'New category' }),
};

/**
 * WIND (contract 3.7): `grant_wind` with block `wind {winds 0.5..3, target}` (rest debt removed; `grant_health` is the old action,
 * kept one content version). The lib/validate lists (stream W1b) gain it on their own branch; the form offers it from here.
 */
export const FORM_TRIGGER_ACTIONS = [...TRIGGER_ACTIONS, 'grant_wind'] as const;
export type FormTriggerAction = (typeof FORM_TRIGGER_ACTIONS)[number];
export const FORM_TRIGGER_BLOCKS: Record<FormTriggerAction, string> = {
  ...TRIGGER_BLOCKS,
  grant_wind: 'wind',
};
/** A trigger as the form edits it: the local TriggerDef plus the wind block. */
export type FormTriggerDef = TriggerDef & { wind?: Schemas['TriggerWindBlock'] | null };

/** The C# defaults of the block an action needs (`raid` gets a raid block when its kind is chosen). */
export function newTriggerBlock(action: FormTriggerAction): object {
  switch (action) {
    case 'grant_wind':
      return { ...triggerWindDefaults(), winds: 1 };
    case 'start_encounter':
      return { ...triggerEncounterDefaults(), kind: 'boss' };
    case 'grant_items':
      return triggerItemsDefaults();
    case 'grant_health':
      return { ...triggerHealthDefaults(), amount: 20 };
    case 'grant_currency':
      return { ...triggerCurrencyDefaults(), amount: 25 };
    case 'grant_cosmetic':
      return { cosmeticId: '' };
    case 'start_mission':
      return { missionId: '' };
    case 'start_event':
      return { ...triggerEventDefaults(), type: 'supply_truck' };
  }
}

/**
 * The trigger with another action: every block is removed and the new action's block starts from its defaults, so the
 * entry always has exactly the one block the validator wants. Switching to the current action keeps the block.
 */
export function withTriggerAction(t: FormTriggerDef, action: string): FormTriggerDef {
  if (t.action === action) return t;
  const next: Record<string, unknown> = { ...t, action };
  for (const key of Object.values(FORM_TRIGGER_BLOCKS)) delete next[key];
  const key = FORM_TRIGGER_BLOCKS[action as FormTriggerAction];
  if (key) Object.assign(next, { [key]: newTriggerBlock(action as FormTriggerAction) });
  return next as FormTriggerDef;
}

/** An encounter block of another kind: only that kind's reference (boss, ambush fight, or raid block) is kept. */
export function withEncounterKind(e: TriggerEncounterBlock, kind: string): TriggerEncounterBlock {
  if (e.kind === kind) return e;
  return {
    kind,
    bossId: kind === 'boss' ? (e.bossId ?? '') : null,
    encounterId: kind === 'ambush' ? (e.encounterId ?? '') : null,
    raid: kind === 'raid' ? (e.raid ?? triggerRaidDefaults()) : null,
  };
}

/** `BossTriggerIds` in shared-cs: `boss_<bossId>` and `mission_<bossId>` (ADR 0029 §10, §16). */
export const bossTriggerId = (bossId: string) => `boss_${bossId}`;
export const bossMissionId = (bossId: string) => `mission_${bossId}`;

/**
 * A boss's trigger and mission, as the seed has them (ADR 0029 §3, §10): `boss_<bossId>` is a repeatable run-scoped
 * `start_encounter` with no cooldown of its own (the boss has its 20 h one) and the boss's code prefix; `mission_<bossId>`
 * is a listed WANTED card after the other missions. Each is null when the bundle already has one with that id.
 */
export function bossTriggerAndMission(
  boss: BossDef,
  bundle: Bundle,
): { trigger: TriggerDef | null; mission: MissionDef | null } {
  const bossId = boss.id ?? '';
  const triggerId = bossTriggerId(bossId);
  const missionId = bossMissionId(bossId);
  const hasTrigger = (bundle.triggers ?? []).some((t) => t?.id === triggerId);
  const hasMission = (bundle.missions ?? []).some((m) => m?.id === missionId);
  const status = boss.status ?? 'coming_soon';
  const order =
    Math.max(0, ...(bundle.missions ?? []).map((m) => (m?.order == null ? 0 : m.order))) + 1;
  return {
    trigger: hasTrigger
      ? null
      : {
          ...triggerDefaults(),
          id: triggerId,
          action: 'start_encounter',
          title: boss.name ?? bossId,
          description: boss.tagline ?? '',
          art: boss.artKey ?? '',
          codePrefix: boss.codePrefix ?? '',
          status,
          missionId,
          rewardsSummary: boss.rewardsSummary ?? '',
          rules: { ...triggerRulesDefaults(), scope: 'run', limit: 'repeatable', cooldownHours: 0 },
          encounter: { kind: 'boss', bossId },
        },
    mission: hasMission
      ? null
      : {
          ...missionDefaults(),
          id: missionId,
          title: boss.name ?? bossId,
          kicker: 'WANTED',
          briefing: boss.lore ?? '',
          art: boss.artKey ?? '',
          triggerId,
          order,
          visibility: 'listed',
          status,
          scope: 'run',
          rewardsSummary: boss.rewardsSummary ?? '',
        },
  };
}
